import { useState } from 'react';

import { Alert, Button, FormField, FormHelp, Heading, Input, Label, Modal, Text } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useInviteToApply } from '@zg-rentals/satellite-components/hooks/useInviteToApply';
import { useRentalManagerUserPropertySummaryWebApiHandler_applications } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import ListingActivationTerms from '../../../ListingActivationTerms';

const HEADING_TEXT = 'Invite a potential tenant to apply';
const INVALID_EMAIL_MSG = 'please enter a valid email address';
const INVALID_NAME_MSG = 'please enter a valid name not exceeding 32 letters';

export const getDisplayName = (firstName: string, lastName: string, email: string): string => {
  if (!firstName) return email;
  if (!lastName) return firstName;
  return `${firstName} ${lastName}`;
};

type Props = {
  listingAlias: string;
  street?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const InviteToApplyModal = ({ listingAlias, street, open, onOpenChange }: Props) => {
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showFailureBanner, setShowFailureBanner] = useState(false);
  const [successBannerText, setSuccessBannerText] = useState('');
  const [errorObj, setErrorObj] = useState({ firstName: '', lastName: '', email: '' });

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setShowSuccessBanner(false);
    setShowFailureBanner(false);
    setSuccessBannerText('');
    setErrorObj({ firstName: '', lastName: '', email: '' });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) resetForm();
    onOpenChange?.(isOpen);
  };

  const { mutate: inviteToApply, isPending: isSubmitting } = useInviteToApply();

  const isErrorFree = Object.values(errorObj).every((v) => !v);
  const isButtonEnabled = !!email && isErrorFree;
  const headingDisplay = street ? `${HEADING_TEXT} to ${street}` : HEADING_TEXT;

  const clearBanners = () => {
    if (showFailureBanner) setShowFailureBanner(false);
    if (showSuccessBanner) setShowSuccessBanner(false);
  };

  const validateName = (name: string) => (!name || name.length < 33 ? '' : INVALID_NAME_MSG);

  const handleSubmission = () => {
    setShowSuccessBanner(false);
    setShowFailureBanner(false);

    inviteToApply(
      {
        postData: {
          listingAlias,
          renterFirstName: firstName,
          renterLastName: lastName,
          toEmail: email,
          resendEmail: false,
        },
      },
      {
        onSuccess: () => {
          setShowSuccessBanner(true);
          setSuccessBannerText(
            `You have invited ${getDisplayName(firstName, lastName, email)} to apply to your rental.`,
          );
          setFirstName('');
          setLastName('');
          setEmail('');
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias }),
          });
        },
        onError: (err) => {
          logError({
            error: err instanceof Error ? err : new Error('Could not invite user to apply.'),
            errorType: 'InviteToApplyModalError',
            context: { listingAlias, details: 'InviteToApplyModal.handleSubmission' },
          });
          setShowFailureBanner(true);
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isButtonEnabled) {
      handleSubmission();
    }
  };

  return (
    <Modal
      data-testid="InviteToApplyModal"
      multiModalTriggerId="InviteToApplyModal"
      {...(open !== undefined && { open })}
      onOpenChange={handleOpenChange}
      header={
        <Heading level={2} textStyle="heading-xs">
          Send application link
        </Heading>
      }
      body={
        <>
          {showSuccessBanner && (
            <Alert tone="success" className="fs-mask">
              {successBannerText}
            </Alert>
          )}

          {showFailureBanner && (
            <Alert tone="critical" className="fs-mask">
              There was an error inviting {getDisplayName(firstName, lastName, email)} to apply to your rental
            </Alert>
          )}

          <Text textStyle="heading-sm" asChild css={{ marginBottom: '400' }}>
            <p>{headingDisplay}</p>
          </Text>

          <FormField css={{ marginBlock: '300' }} error={!!errorObj.email}>
            <Label required={true}>Email</Label>
            <Input
              aria-label="Email"
              onBlur={() =>
                setErrorObj((prev) => ({
                  ...prev,
                  email: /(.+?)@(.+?)\.[a-zA-Z]{1,}/.test(email) ? '' : INVALID_EMAIL_MSG,
                }))
              }
              onChange={(e) => {
                clearBanners();
                setEmail(e.target.value);
              }}
              value={email}
              onKeyDown={handleKeyDown}
              className="fs-mask"
            />
            <FormHelp>{errorObj.email}</FormHelp>
          </FormField>

          <FormField css={{ marginBlock: '300' }} error={!!errorObj.firstName}>
            <Label>First name</Label>
            <Input
              aria-label="First name"
              onBlur={() => setErrorObj((prev) => ({ ...prev, firstName: validateName(firstName) }))}
              onChange={(e) => {
                clearBanners();
                setFirstName(e.target.value);
              }}
              value={firstName}
              className="fs-mask"
            />
            <FormHelp>{errorObj.firstName}</FormHelp>
          </FormField>

          <FormField css={{ marginBlock: '300' }} error={!!errorObj.lastName}>
            <Label>Last name</Label>
            <Input
              aria-label="Last name"
              onBlur={() => setErrorObj((prev) => ({ ...prev, lastName: validateName(lastName) }))}
              onChange={(e) => {
                clearBanners();
                setLastName(e.target.value);
              }}
              value={lastName}
              className="fs-mask"
            />
            <FormHelp>{errorObj.lastName}</FormHelp>
          </FormField>

          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleSubmission}
            disabled={!isButtonEnabled}
            loading={isSubmitting}
            fluid
            css={{ marginBlock: '200' }}
          >
            Send
          </Button>

          <ListingActivationTerms actionText="By clicking Send" />
        </>
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default InviteToApplyModal;
